import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import {
  AiMessageCountEntityComponent,
  ComponentEntityType,
} from '@texas/api/endpoints/chat/componentsApi';
import { VariantsSearchQuery } from '@texas/api/endpoints/search/searchVariantsTypes';
import { PaTableColumns } from '@texas/components/dashboard/PaTableColumns';
import { TableColumns } from '@texas/components/searchArticles/TableColumns';
import { Icons } from '@texas/components/shared/Icons';
import { ArticlesOverviewModal } from '@texas/components/widgets/shared/components/articles/ArticlesOverviewModal';
import { PaOverviewModal } from '@texas/components/widgets/shared/components/productApprovals/PaOverviewModal';
import { ensureEnumNumber } from '@texas/utils/helpers/enumHelpers';
import { useTranslation } from 'react-i18next';

const defaultParams = {
  page: 1,
  searchTerm: '',
  sortBy: '',
  sortDesc: false,
};

export function CountEntity({
  component,
}: {
  component: AiMessageCountEntityComponent;
}) {
  const { isOpen, onClose, onOpen } = useDisclosure();
  const { t } = useTranslation();

  const ensuredType = ensureEnumNumber(ComponentEntityType, component.type);

  return (
    <Box
      borderLeft="2px solid white"
      pl={3}
      pb={2}
      cursor="pointer"
      transition="padding-left 200ms ease"
      _hover={{ pl: 4 }}
      onClick={onOpen}
    >
      <Flex alignItems="baseline" gap={2}>
        <Text fontSize="3xl" fontFamily="barlow" fontWeight="bold">
          {component.entityIds.length}
        </Text>
        <Text>{component.blobPreviewText ?? component.label}</Text>
      </Flex>
      <Flex gap={2} alignItems="center" pt={2} opacity={0.7}>
        <Icons.OpenInNew />
        <Text fontSize="sm">{t('widgets.showResult')}</Text>
      </Flex>
      {ensuredType === ComponentEntityType.Article && (
        <ArticlesOverviewModal
          isOpen={isOpen}
          onClose={onClose}
          filters={{
            ...defaultParams,
            articleIds: component.entityIds,
          }}
          articleSearchProps={{
            filters: [],
            searchParams: {
              ...parseBlob<VariantsSearchQuery>(
                component.blobData,
                defaultParams,
              ),
              articleIds: component.entityIds,
            },
          }}
          columns={TableColumns}
        />
      )}
      {ensuredType === ComponentEntityType.ProductDevelopmentVariant && (
        <ArticlesOverviewModal
          isOpen={isOpen}
          onClose={onClose}
          filters={{
            ...defaultParams,
            variantIds: component.entityIds,
          }}
          articleSearchProps={{
            filters: [],
            searchParams: {
              ...parseBlob<VariantsSearchQuery>(
                component.blobData,
                defaultParams,
              ),
              variantIds: component.entityIds,
            },
          }}
          columns={() => TableColumns(t, true)}
          type="slim"
        />
      )}
      {ensuredType === ComponentEntityType.ArticleVariant && (
        <ArticlesOverviewModal
          isOpen={isOpen}
          onClose={onClose}
          filters={{
            ...defaultParams,
            variantIds: component.entityIds,
          }}
          articleSearchProps={{
            filters: [],
            searchParams: {
              ...parseBlob<VariantsSearchQuery>(
                component.blobData,
                defaultParams,
              ),
              variantIds: component.entityIds,
            },
          }}
          columns={TableColumns}
        />
      )}
      {ensuredType === ComponentEntityType.ProductApproval && (
        <PaOverviewModal
          isOpen={isOpen}
          onClose={onClose}
          filters={{
            searchTerm: '',
            productApprovalIds: component.entityIds,
          }}
          searchProps={{
            filters: [],
            searchParams: {
              ...defaultParams,
              productApprovalIds: component.entityIds,
            },
          }}
          columns={PaTableColumns}
        />
      )}
    </Box>
  );
}

function parseBlob<T>(data: string | undefined, defaultValue: T): T {
  if (!data) return defaultValue;
  try {
    const parsed = JSON.parse(data);
    const normalized = normalizeKeys(parsed);
    return { ...normalized, ...defaultValue } as T;
  } catch {
    return defaultValue;
  }
}
function toCamelCase(str: string) {
  return str.charAt(0).toLowerCase() + str.slice(1);
}

function normalizeKeys(obj: any): any {
  if (Array.isArray(obj)) {
    return obj.map(normalizeKeys);
  }
  if (obj !== null && typeof obj === 'object') {
    return Object.fromEntries(
      Object.entries(obj).map(([key, value]) => [
        toCamelCase(key),
        normalizeKeys(value),
      ]),
    );
  }
  return obj;
}
